import { Flex, Form, Input, Typography } from 'antd';
import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import { confirmClientInvitation } from '@/api/client';
import { updateRecruiter } from '@/api/recruiter';
import { updateUser } from '@/api/user';
import { buildPersonalInfoRecruiterFieldDefs } from '@/components/form/fieldDefs/personalInfoRecruiterFields';
import {
  ACCEPTED_PRIVACY_POLICY_FIELD,
  buildSignUpPrivacyAcceptanceSection,
} from '@/components/form/fieldDefs/signUpPrivacyAcceptance';
import { fieldDefToFieldConfig } from '@/components/form/fieldDefs/types';
import { buildUserPreferenceSection } from '@/components/form/fieldDefs/userPreferenceFields';
import SchemaForm from '@/components/form/SchemaForm/SchemaForm';
import { LOGIN, DASHBOARD } from '@/constants/routes';
import { useApiMutation } from '@/hooks/api';
import { useRefreshSessionUser } from '@/hooks/useRefreshSessionUser';
import { useSupportedLanguages } from '@/hooks/useSupportedLanguages';
import { useUrlState } from '@/hooks/useUrlState';
import { tCatalog } from '@/i18n/tCatalog';
import { queryKeys } from '@/shared/lib/queryKeys';
import { useAppDispatch } from '@/store/hooks';
import { login } from '@/store/userSlice';
import FormErrorAlert from '@/UI/FormErrorAlert';
import { PageTitle } from '@/UI/typography';
import { appendFormDataValue } from '@/utils/formDataAppend';
import { storeAccessToken } from '@/utils/localStorage';
import { buildRecruiterInvitationFormSchema } from '@/utils/validation/formSchemas/recruiterSchemas';

import type { SchemaSection } from '@/components/form/SchemaForm/types';
import type { UrlParams } from '@/pages/InvitationConfirm';
import type { InvitedUserDetails, User } from '@/types/user';

type ConfirmedInviteSession = {
  access_token: string;
  user: User;
};

type RecruiterInvitationFormValues = {
  image?: string;
  name?: string;
  title?: string;
  company?: string;
  city?: string;
  email?: string;
  username?: string;
  phone?: string;
  password?: string;
  password_confirm?: string;
  currency?: string;
  language?: string;
  accepted_privacy_policy?: boolean;
  recruiter_type?: string;
  description?: string;
  about_company?: string;
  preferable_specializations?: string[];
};

type RecruiterInvitationFormProps = {
  user: InvitedUserDetails;
  onInvitationComplete?: () => void;
};

const RecruiterInvitationForm = ({
  user,
  onInvitationComplete,
}: RecruiterInvitationFormProps) => {
  const [showCompany, setShowCompany] = useState<boolean>(true);
  const [confirmedSession, setConfirmedSession] =
    useState<ConfirmedInviteSession | null>(null);
  const [fileError, setFileError] = useState<string>('');
  const { uid, token } = useParams() as unknown as UrlParams;
  const dispatch = useAppDispatch();
  const refreshSessionUser = useRefreshSessionUser();
  const { history } = useUrlState();
  const [form] = Form.useForm<RecruiterInvitationFormValues>();
  const hydratedUserIdRef = useRef<string | null>(null);
  const [t] = useTranslation();
  const { languages } = useSupportedLanguages();
  const formSchema = useMemo(() => buildRecruiterInvitationFormSchema(t), [t]);

  const handleValueChange = (changedValues: Record<string, unknown>) => {
    if ('recruiter_type' in changedValues) {
      setShowCompany(changedValues.recruiter_type !== 'freelance');
    }
    if ('image' in changedValues) {
      setFileError('');
    }
  };

  const handleInvalidImageFormat = useCallback(() => {
    setFileError(t('FILE_FORMAT_VALIDATION'));
  }, [t]);

  const recruiterPersonalFields = useMemo(
    () =>
      buildPersonalInfoRecruiterFieldDefs({
        t,
        showCompany,
        isOnboarding: true,
        hasCompany: !!user?.company,
        companyLocked: !!user?.pending_recruiter_invitation,
      }).map(fieldDefToFieldConfig),
    [t, showCompany, user?.company, user?.pending_recruiter_invitation],
  );

  const sections = useMemo<SchemaSection[]>(
    () => [
      {
        key: 'profile',
        fields: [
          {
            name: 'image',
            type: 'image',
            label: t('PROFILE_IMAGE'),
            help: fileError || undefined,
            validateStatus: fileError ? 'error' : undefined,
            componentProps: {
              name: t('PROFILE_IMAGE'),
              onInvalidFormat: handleInvalidImageFormat,
            },
          },
          ...recruiterPersonalFields,
        ],
      },
      {
        key: 'credentials',
        fields: [
          {
            key: 'email-hidden',
            type: 'custom',
            render: () => (
              <Form.Item name="email" className="hidden">
                <Input type="email" />
              </Form.Item>
            ),
          },
          {
            name: 'password',
            type: 'password',
            label: t('PASSWORD_LABEL'),
            help: t('PASSWORD_HINT'),
            colSpan: 12,
          },
          {
            name: 'password_confirm',
            type: 'password',
            label: t('PASSWORD_CONFIRM_LABEL'),
            colSpan: 12,
          },
        ],
      },
      buildUserPreferenceSection({
        t,
        languageOptions: languages.map((lang) => ({
          value: lang.code,
          label: tCatalog('languages', lang.name),
        })),
      }),
      buildSignUpPrivacyAcceptanceSection(),
    ],
    [
      t,
      fileError,
      handleInvalidImageFormat,
      languages,
      recruiterPersonalFields,
    ],
  );

  const onFinishMutation = useApiMutation<
    void,
    RecruiterInvitationFormValues,
    unknown
  >({
    mutationFn: async (values) => {
      const submissionValues: Record<string, unknown> = { ...values };
      delete submissionValues.password_confirm;
      delete submissionValues[ACCEPTED_PRIVACY_POLICY_FIELD];
      // Never send the locked display name — that would rename a placeholder
      // company to the project/agency name.
      if (user?.pending_recruiter_invitation) {
        delete submissionValues.company;
      } else if (!values.company) {
        const companyName = (user?.company as { name?: string } | undefined)
          ?.name;
        if (companyName) {
          submissionValues.company = companyName;
        }
      }
      const imageValue = values.image;
      if (!imageValue || imageValue.startsWith('http')) {
        delete submissionValues.image;
      }

      const formData = new FormData();
      Object.entries(submissionValues).forEach(([key, value]) => {
        appendFormDataValue(formData, key, value);
      });

      let session = confirmedSession;

      if (!session) {
        const confirmData = {
          uid,
          token,
          new_password: submissionValues.password as string,
        };
        const data = await confirmClientInvitation(confirmData);
        if (!data?.access_token || !data?.user) {
          throw new Error(t('ERROR_SOMETHING_WENT_WRONG'));
        }
        session = {
          access_token: data.access_token,
          user: data.user,
        };
        setConfirmedSession(session);
      }

      storeAccessToken(session.access_token);

      await updateRecruiter(formData);
      if (values.language || values.currency) {
        await updateUser({
          default_language: values.language,
          default_currency: values.currency,
        });
      }
      dispatch(
        login({
          user: session.user,
          access_token: session.access_token,
        }),
      );
      await refreshSessionUser();
      onInvitationComplete?.();
      history.push(DASHBOARD);
    },
    invalidateKeys: [queryKeys.user.recruiterInfo()],
  });

  const onFinish = (values: RecruiterInvitationFormValues) => {
    onFinishMutation.mutate(values);
  };

  // Route-only onboarding: hydrates once per user id. Add an `open`/`mountKey`
  // guard before reusing this form inside a modal or drawer.
  useEffect(() => {
    if (!user?.id) return;
    const identityKey = String(user.id);
    if (hydratedUserIdRef.current === identityKey) return;
    hydratedUserIdRef.current = identityKey;

    const pending = user.pending_recruiter_invitation;
    const companyName =
      pending?.is_client_owned && pending.client_company_name
        ? t('PROJECT_FOR_CLIENT', {
            agencyCompany: pending.company_name,
            client: pending.client_company_name,
          })
        : pending?.company_name ||
          (user.company as { name?: string } | undefined)?.name;

    form.setFieldsValue({
      name: user.name,
      title: user.title,
      city: '',
      recruiter_type: user.recruiter_type ?? undefined,
      company: companyName,
      email: user.email,
      username: user.email,
      phone: user.phone,
    });
    if (user.recruiter_type === 'freelance') {
      setShowCompany(false);
    }
  }, [user, form, t]);

  return (
    <>
      <Flex justify="space-between" align="center">
        <PageTitle>{t('PERSONAL_INFO_TITLE')} </PageTitle>
        <Typography.Text type="secondary">
          {t('HAVE_ACCOUNT_QUESTION')}{' '}
          <Link to={LOGIN}>{t('LOG_IN_TEXT')}</Link>
        </Typography.Text>
      </Flex>
      <FormErrorAlert error={onFinishMutation.error} />
      <SchemaForm
        form={form}
        formSchema={formSchema}
        name="recruiter-invite"
        scrollToFirstError
        onFinish={onFinish}
        onValuesChange={handleValueChange}
        sections={sections}
        footer={{
          actions: [
            {
              label: t('SIGN_UP_TITLE'),
              htmlType: 'submit',
              type: 'primary',
              loading: onFinishMutation.loading,
            },
          ],
        }}
      />
    </>
  );
};

export default RecruiterInvitationForm;
